// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

const US_DIGIT_COUNT = 10;
const NPI_LUHN_PREFIX = '80840';

function digitsOf(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\D/g, '');
}

export function isUsPhone10(raw: string | null | undefined): boolean {
  const digits = digitsOf(raw);
  if (digits.length === US_DIGIT_COUNT) {
    return true;
  }
  if (digits.length === US_DIGIT_COUNT + 1 && digits.startsWith('1')) {
    return true;
  }
  return false;
}

export function normalizeUsPhoneToE164(raw: string | null | undefined): string | undefined {
  const digits = digitsOf(raw);
  if (digits.length === US_DIGIT_COUNT) {
    return `+1${digits}`;
  }
  if (digits.length === US_DIGIT_COUNT + 1 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  return undefined;
}

export function formatUsPhoneDisplay(raw: string | null | undefined): string {
  const digits = digitsOf(raw);
  const ten = digits.length === US_DIGIT_COUNT + 1 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== US_DIGIT_COUNT) {
    return raw ?? '';
  }
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export function isNpi10(raw: string | null | undefined): boolean {
  return /^\d{10}$/.test((raw ?? '').trim());
}

/**
 * NPI Luhn check per CMS: prefix the 9-digit identifier (first 9 of the 10)
 * with `80840`, then validate the 10th digit as Luhn check digit over the
 * resulting 14-digit string.
 */
export function isNpiLuhnValid(raw: string | null | undefined): boolean {
  const trimmed = (raw ?? '').trim();
  if (!isNpi10(trimmed)) {
    return false;
  }
  const body = NPI_LUHN_PREFIX + trimmed.slice(0, 9);
  const checkDigit = Number(trimmed.slice(9));
  let sum = 0;
  for (let i = body.length - 1, double = true; i >= 0; i--, double = !double) {
    let n = Number(body[i]);
    if (double) {
      n *= 2;
      if (n > 9) {
        n -= 9;
      }
    }
    sum += n;
  }
  const computed = (10 - (sum % 10)) % 10;
  return computed === checkDigit;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmailLike(raw: string | null | undefined): boolean {
  return EMAIL_REGEX.test((raw ?? '').trim());
}
